// src/hooks/useAdminWebSocket.js
import { useEffect, useRef, useState, useCallback } from 'react';

const getWsUrl = () => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'ws://localhost:8080';
  } else if (hostname === '192.168.0.175') {
      return 'ws://192.168.0.175:8080';
  } else {
      // Production
      return `wss://${window.location.host}/ws`;
  }
};

const WS_URL = getWsUrl();

export const useAdminWebSocket = () => {
  const wsRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const messageHandlers = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const isConnectingRef = useRef(false);
  
  // Handle incoming messages for ADMIN
  const handleIncomingMessage = useCallback((data) => {
    console.log('📨 [Admin] Received:', data.type);
    
    switch (data.type) {
      case 'new_support_message':
        // For regular messages from users
        const ticketHandler = messageHandlers.current.get(`support_${data.ticket_id}`);
        if (ticketHandler) {
            ticketHandler(data.message);
        }
        // Also notify for new tickets dashboard
        const newTicketHandler = messageHandlers.current.get('new_tickets');
        if (newTicketHandler && data.message.sender_type === 'user') {
            newTicketHandler(data);
        }
        break;
          
      case 'support_ticket_status':
        const statusHandler = messageHandlers.current.get(`ticket_status_${data.ticket_id}`);
        if (statusHandler) {
            statusHandler(data);
        }
        break;
        
      case 'ticket_assigned':
        console.log(`Ticket ${data.ticket_id} assigned to admin ${data.assigned_to}`);
        const assignHandler = messageHandlers.current.get(`ticket_assigned_${data.ticket_id}`);
        if (assignHandler) {
            assignHandler(data);
        }
        // Also notify dashboard
        const dashboardHandler = messageHandlers.current.get('ticket_updates');
        if (dashboardHandler) {
            dashboardHandler(data);
        }
        break;
        
      case 'connected':
        console.log('✅ Admin connected as user:', data.user_id);
        break;
        
      case 'subscribed':
        console.log('✅ Admin subscribed to conversation:', data.conversation_id);
        break;

      case 'pong':
        break;
        
      case 'error':
        console.error('WebSocket error:', data.message);
        break;
        
      default:
        console.log('Unknown message type:', data.type);
    }
  }, []);
  
  // Connect to WebSocket
  const connect = useCallback(() => {
    if (isConnectingRef.current) {
      console.log('⚠️ Already connecting, skipping...');
      return;
    }
    
    // Admin uses admin token
    const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
    if (!token) {
      console.warn('No admin token found');
      return;
    }
    
    // Close existing connection
    if (wsRef.current) {
      console.log('Closing existing connection...');
      wsRef.current.close();
    }
    
    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    isConnectingRef.current = true;
    
    const wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}`;
    console.log('🔌 [Admin] Connecting to:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('✅ [Admin] WebSocket connected');
      setIsConnected(true);
      isConnectingRef.current = false;
      
      // Send initial ping
      setTimeout(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 500);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleIncomingMessage(data);
      } catch (error) {
        console.error('Failed to parse message:', error);
      }
    };
    
    ws.onclose = (event) => {
      console.log('🔌 [Admin] WebSocket disconnected:', event.code, event.reason);
      setIsConnected(false);
      isConnectingRef.current = false;
      
      // Don't reconnect if closed normally (1000) or on purpose
      if (event.code !== 1000 && event.code !== 1001) {
        // Try to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('🔄 [Admin] Attempting to reconnect...');
          connect();
        }, 5000);
      }
    };
    
    ws.onerror = (error) => {
      console.error('[Admin] WebSocket error:', error);
      isConnectingRef.current = false;
    };
  }, [handleIncomingMessage]);
  
  // Subscribe to a support ticket (same as user)
  const subscribeToSupportTicket = useCallback((ticketId, onMessage) => {
    const key = `support_${ticketId}`;
    messageHandlers.current.set(key, onMessage);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
            type: 'subscribe_support_ticket',
            ticket_id: ticketId
        }));
    }
    
    return () => {
        messageHandlers.current.delete(key);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'unsubscribe_support_ticket',
                ticket_id: ticketId
            }));
        }
    };
  }, []);
  
  // ADMIN ONLY: Subscribe to new tickets for dashboard
  const subscribeToNewTickets = useCallback((onNewTicket) => {
    const key = 'new_tickets';
    messageHandlers.current.set(key, onNewTicket);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
            type: 'subscribe_admin_tickets'
        }));
    }
    
    return () => {
        messageHandlers.current.delete(key);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'unsubscribe_admin_tickets'
            }));
        }
    };
  }, []);
  
  // ADMIN ONLY: Subscribe to all ticket updates for dashboard
  const subscribeToTicketUpdates = useCallback((onUpdate) => {
    const key = 'ticket_updates';
    messageHandlers.current.set(key, onUpdate);
    
    return () => {
        messageHandlers.current.delete(key);
    };
  }, []);
  
  // Send ping (keep-alive)
  const sendPing = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    
    try {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    } catch (error) {
      console.error('Failed to send ping:', error);
    }
  }, []);
  
  // Connect only once on mount
  useEffect(() => {
    connect();
    
    // Set up ping every 25 seconds
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        sendPing();
      }
    }, 25000);
    
    // Cleanup
    return () => {
      clearInterval(pingInterval);
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      
      messageHandlers.current.clear();
      isConnectingRef.current = false;
    };
  }, [connect, sendPing]);
  
  return {
    isConnected,
    subscribeToSupportTicket,
    subscribeToNewTickets,
    subscribeToTicketUpdates,
    reconnect: connect
  };
};